import React from 'react'
import styled, { useTheme } from 'styled-components/native'
import { ImageURISource } from 'react-native'
import useImageDimensions from 'APP/Hooks/useImageDimensions'

interface LogoBaseProps {
  height: number
  testID?: string
  alt?: string
}

const LogoImage = styled.Image<{ aspectRatio: number; height: number }>`
  height: ${({ height }) => height}px;
  max-height: ${({ height }) => height}px;
  width: auto;
  max-width: 200px;
  resize-mode: contain;
  aspect-ratio: ${({ aspectRatio }) => aspectRatio};
`

const SvgContainer = styled.View<{ height: number }>`
  height: ${({ height }) => height}px;
  max-height: ${({ height }) => height}px;
  max-width: 200px;
  justify-content: center;
  align-items: start;
`

const BitmapLogo = ({ source, ...props }: LogoBaseProps & { source: ImageURISource }) => {
  const { aspectRatio, isLoading } = useImageDimensions(source)

  if (isLoading) return <></>

  return (
    <LogoImage
      {...props}
      aspectRatio={aspectRatio}
      source={source}
      accessibilityRole="image"
      accessibilityLabel={props.alt}
      alt={props.alt}
    />
  )
}

const SVGLogo = ({
  source: Source,
  ...props
}: LogoBaseProps & { source: React.ElementType | React.ReactNode }) => {
  const theme = useTheme()
  const content =
    typeof Source === 'function' ? (
      <Source
        height={props.height}
        fillLine={theme.colors.text}
        accentFillLine={theme.colors.accent}
      />
    ) : (
      Source
    )

  return (
    <SvgContainer {...props} accessibilityRole="image" accessibilityLabel={props.alt}>
      {content}
    </SvgContainer>
  )
}

const Logo = ({
  source,
  height = 45,
  ...props
}: {
  source: ImageURISource | React.ReactNode | React.ComponentType<any>
  height?: number
  testID?: string
  alt?: string
}) => {
  const logoProps = { height, ...props }

  return typeof source === 'function' || React.isValidElement(source) ? (
    <SVGLogo source={source} {...logoProps} />
  ) : (
    <BitmapLogo source={source as ImageURISource} {...logoProps} />
  )
}

export default Logo
